import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, Heart, Star, ChevronDown,
  ClipboardList, Monitor, HeartPulse, Stethoscope, Clipboard, Brain, Plus, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { clinicInfo, banners, services, history, team, insurancePlans, whyChoose, sections } from '../data/content';

const iconMap = { ClipboardList, Monitor, HeartPulse, Stethoscope, Clipboard, Brain, Plus, FileText, Heart };

const LandingPage = () => {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [insuranceOpen, setInsuranceOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBanner((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, []);

  const handleWhatsAppClick = () => {
    window.open('https://api.whatsapp.com/send/?phone=552422432123&text&type=phone_number&app_absent=0', '_blank');
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  };

  const navItems = [
    { label: "Nossos Serviços", id: "services" },
    { label: "Nossa História", id: "history" },
    { label: "Nossa Equipe", id: "team" },
    { label: "Convênios", id: "insurance" },
    { label: "Por que escolher o CICARPE?", id: "why-choose" },
    { label: "Contato", id: "contact" }
  ];

  const renderIcon = (iconName) => {
    const IconComp = iconMap[iconName] || Heart;
    return <IconComp size={32} style={{ color: 'white' }} />;
  };

  return (
    <div className="landing-page" data-testid="landing-page">
      {/* Header */}
      <header className="header-nav" data-testid="header-nav">
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
          <img
            src={clinicInfo.logo}
            alt={clinicInfo.name}
            style={{ maxHeight: '50px', objectFit: 'contain' }}
            data-testid="header-logo"
          />

          {/* Desktop Menu */}
          <div style={{ display: 'none' }} className="desktop-menu">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => { e.preventDefault(); scrollTo(item.id); }}
                style={{ textDecoration: 'none', color: 'var(--text-primary)', fontSize: '0.875rem', cursor: 'pointer' }}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </a>
            ))}
            <Button className="btn-primary" onClick={handleWhatsAppClick} data-testid="nav-agendar-btn">AGENDAR</Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ display: 'none', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-primary)', padding: '0.5rem' }}
            className="mobile-menu-btn"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? '\u2715' : '\u2630'}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div
            style={{ display: 'none', position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', padding: '1rem', zIndex: 1000 }}
            className="mobile-menu-dropdown"
            data-testid="mobile-menu-dropdown"
          >
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => { e.preventDefault(); scrollTo(item.id); }}
                style={{ display: 'block', padding: '0.75rem 0', textDecoration: 'none', color: 'var(--text-primary)', borderBottom: '1px solid #eee' }}
              >
                {item.label}
              </a>
            ))}
            <Button
              className="btn-primary"
              onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }}
              style={{ width: '100%', marginTop: '1rem' }}
            >
              AGENDAR
            </Button>
          </div>
        )}
      </header>

      {/* Hero Banner */}
      <section className="hero-section" data-testid="hero-section" style={{ position: 'relative', height: isMobile ? '500px' : '600px', overflow: 'hidden', marginTop: '80px' }}>
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              opacity: currentBanner === index ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
            }}
          >
            <img
              src={banner.image}
              alt={banner.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: isMobile
                ? 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.7) 100%)'
                : 'linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%)',
              display: 'flex',
              alignItems: isMobile ? 'flex-end' : 'center',
              padding: isMobile ? '2rem 1rem' : '0',
            }}>
              <div className="container" style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  maxWidth: isMobile ? '100%' : '600px',
                  color: banner.text_color || 'white',
                  textAlign: isMobile ? 'center' : 'left'
                }}>
                  <h1 className="heading-hero" style={{
                    marginBottom: '1rem',
                    color: banner.text_color || 'white',
                    fontSize: isMobile ? '1.75rem' : undefined
                  }}>
                    {banner.title}
                  </h1>
                  {banner.subtitle && (
                    <p className="body-large" style={{
                      marginBottom: '1.5rem', opacity: 0.95,
                      color: banner.text_color || 'white',
                      fontSize: isMobile ? '0.95rem' : undefined
                    }}>
                      {banner.subtitle}
                    </p>
                  )}
                  {banner.show_button && (
                    <button
                      onClick={handleWhatsAppClick}
                      className="btn-primary"
                      style={{ cursor: 'pointer', border: 'none' }}
                      data-testid={`banner-cta-${index}`}
                    >
                      {banner.button_text || 'Agendar Consulta'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Nossos Servicos */}
      <section id="services" data-testid="services-section" style={{ background: 'var(--bg-section)', padding: '5rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1">Nossos Serviços</h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>
              Oferecemos uma gama completa de serviços médicos especializados
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            {services.map((service) => (
              <div key={service.id} data-testid={`service-card-${service.id}`} style={{
                background: 'white', padding: '2rem', borderRadius: '1rem',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.03)'; }}
              >
                <div style={{
                  width: '60px', height: '60px', background: '#3b82f6', borderRadius: '12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem',
                }}>
                  {renderIcon(service.icon)}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{service.title}</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nossa Historia */}
      <section id="history" data-testid="history-section" style={{ padding: '5rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1">{history.title}</h2>
          </div>
          <div
            className="body-medium"
            style={{
              color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto',
              whiteSpace: 'pre-wrap', textAlign: 'left', lineHeight: '1.8'
            }}
          >
            {history.content}
          </div>
        </div>
      </section>

      {/* Nossa Equipe */}
      <section id="team" data-testid="team-section" style={{ background: 'var(--bg-section)', padding: '5rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1">Nossa Equipe</h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>
              Profissionais experientes dedicados ao seu bem-estar
            </p>
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2.5rem', maxWidth: '800px', margin: '0 auto'
          }}>
            {team.map((member) => (
              <div key={member.id} data-testid={`team-member-${member.id}`} style={{ textAlign: 'center' }}>
                <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>{member.name}</h3>
                <p className="body-medium" style={{ color: 'var(--primary-blue)' }}>{member.specialty}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Convenios Aceitos */}
      <section id="insurance" data-testid="insurance-section" style={{ padding: '5rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h2 className="heading-1">Convênios Aceitos</h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>
              Trabalhamos com os principais planos de saúde
            </p>
          </div>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <button
              onClick={() => setInsuranceOpen(!insuranceOpen)}
              data-testid="insurance-dropdown-btn"
              style={{
                width: '100%', padding: '1rem 1.5rem',
                background: 'white', border: '1px solid var(--border-primary)',
                borderRadius: insuranceOpen ? '0.75rem 0.75rem 0 0' : '0.75rem',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontSize: '1rem', fontWeight: '500', color: 'var(--text-primary)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                transition: 'box-shadow 0.2s ease',
              }}
            >
              <span>Convênios Aceitos ({insurancePlans.length})</span>
              <ChevronDown
                size={20}
                style={{
                  transition: 'transform 0.3s ease',
                  transform: insuranceOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: 'var(--primary-blue)'
                }}
              />
            </button>
            {insuranceOpen && (
              <div
                data-testid="insurance-dropdown-list"
                style={{
                  background: 'white', border: '1px solid var(--border-primary)', borderTop: 'none',
                  borderRadius: '0 0 0.75rem 0.75rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  maxHeight: '400px', overflowY: 'auto',
                }}
              >
                {insurancePlans.map((plan, index) => (
                  <div
                    key={index}
                    data-testid={`insurance-item-${index}`}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderBottom: index < insurancePlans.length - 1 ? '1px solid #f1f5f9' : 'none',
                      fontSize: '0.9rem', color: 'var(--text-secondary)',
                    }}
                  >
                    {plan}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Entre em Contato */}
      <section id="contact" data-testid="contact-section" className="container" style={{ padding: '5rem 1.2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem' }}>
          <div>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>Entre em Contato</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <MapPin size={24} style={{ color: 'var(--text-primary)', flexShrink: 0 }} />
                <div>
                  <p className="body-medium" style={{ fontWeight: '500', marginBottom: '0.25rem' }}>Endereço</p>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{clinicInfo.address}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <Phone size={24} style={{ color: 'var(--text-primary)', flexShrink: 0 }} />
                <div>
                  <p className="body-medium" style={{ fontWeight: '500', marginBottom: '0.25rem' }}>Telefones</p>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{clinicInfo.phone}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <Mail size={24} style={{ color: 'var(--text-primary)', flexShrink: 0 }} />
                <div>
                  <p className="body-medium" style={{ fontWeight: '500', marginBottom: '0.25rem' }}>Email</p>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{clinicInfo.email}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <Clock size={24} style={{ color: 'var(--text-primary)', flexShrink: 0 }} />
                <div>
                  <p className="body-medium" style={{ fontWeight: '500', marginBottom: '0.25rem' }}>Horários</p>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{clinicInfo.hours.weekdays}</p>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{clinicInfo.hours.saturday}</p>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{clinicInfo.hours.sunday}</p>
                </div>
              </div>
            </div>
          </div>

          <Card style={{ background: '#25D366', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem 2rem' }}>
            <CardContent style={{ textAlign: 'center' }}>
              <MessageCircle size={64} className="mx-auto mb-4" />
              <h3 className="heading-2" style={{ color: 'white', marginBottom: '1rem' }}>Agende pelo WhatsApp</h3>
              <p className="body-medium" style={{ color: 'rgba(255,255,255,0.95)', marginBottom: '2rem' }}>
                Clique no botão abaixo para conversar com nossa equipe e agendar sua consulta rapidamente
              </p>
              <Button
                onClick={handleWhatsAppClick}
                data-testid="whatsapp-card-btn"
                style={{
                  background: 'white', color: '#25D366', fontWeight: '600',
                  padding: '1rem 2rem', fontSize: '1rem', border: 'none', cursor: 'pointer'
                }}
              >
                <MessageCircle size={20} style={{ marginRight: '0.5rem' }} />
                Abrir WhatsApp
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Por que escolher o CICARPE */}
      <section id="why-choose" data-testid="why-choose-section" style={{ background: 'var(--bg-section)', padding: '5rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1">{whyChoose.title}</h2>
          </div>
          <div
            className="body-medium"
            style={{
              color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto',
              whiteSpace: 'pre-wrap', textAlign: 'left', lineHeight: '1.8'
            }}
          >
            {whyChoose.content}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="footer" style={{ background: 'var(--text-primary)', color: 'white', padding: '3rem 0 1.5rem' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
            <div>
              <h3 className="heading-3" style={{ color: 'white', marginBottom: '1rem' }}>Cicarpe</h3>
              <p className="body-small" style={{ color: 'rgba(255,255,255,0.8)', lineHeight: '1.6' }}>
                {clinicInfo.tagline}
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem' }}>Links Rápidos</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[
                  { label: "Serviços", id: "services" },
                  { label: "Equipe", id: "team" },
                  { label: "Contato", id: "contact" }
                ].map((link) => (
                  <a
                    key={link.id}
                    href={`#${link.id}`}
                    onClick={(e) => { e.preventDefault(); scrollTo(link.id); }}
                    style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '0.875rem', cursor: 'pointer' }}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem' }}>Contato</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <p className="body-small" style={{ color: 'rgba(255,255,255,0.8)' }}>{clinicInfo.phone}</p>
                <p className="body-small" style={{ color: 'rgba(255,255,255,0.8)' }}>{clinicInfo.email}</p>
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '1.5rem', textAlign: 'center' }}>
            <p className="body-small" style={{ color: 'rgba(255,255,255,0.6)' }}>
              &copy; 2025 Cicarpe. Todos os direitos reservados.
            </p>
            <p className="body-small" style={{ color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem' }}>
              {clinicInfo.address}
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button
        onClick={handleWhatsAppClick}
        data-testid="floating-whatsapp-btn"
        style={{
          position: 'fixed', bottom: '2rem', right: '2rem', width: '60px', height: '60px',
          borderRadius: '50%', background: '#25D366', border: 'none', cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease', zIndex: 1000
        }}
        onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)'; }}
        onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
      >
        <MessageCircle size={32} color="white" />
      </button>
    </div>
  );
};

export default LandingPage;
